import React from 'react'
import { styled } from '@mui/material/styles'
import {
    Avatar,
    Box,
    Button,
    Chip,
    Container,
    IconButton,
    Link as MuiLink,
    Tooltip,
    Typography,
    colors,
} from '@mui/material'
import moment from 'moment'
import ReactMarkdown from 'react-markdown'
import { getMediaUrl } from '@assembly/utils'
import LinkIcon from '@mui/icons-material/Link'
import logoWhite from '@assembly/assets/logo-white.png'
import VideoAndAssessmentTabs from './VideoAndAssessmentTabs'
import ContactCandidateDialog from './ContactCandidateDialog'

const StyledBox = styled(Box)(() => ({
    border: '1px solid',
    borderColor: colors.grey[200],
    borderRadius: '10px',
    marginTop: '10px',
    marginBottom: '10px',
}))

const MainInfoBox = styled(Box)(() => ({
    background: '#FFFFFF',
    borderRadius: '10px',
    position: 'relative',
    paddingBottom: '30px',
}))

const BannerGradient = styled(Box)(({ theme }) => ({
    height: '200px',
    background: '#4F59CC',
    borderTopRightRadius: '10px',
    borderTopLeftRadius: '10px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
        height: '150px',
    },
}))

const ProfilePicture = styled(Avatar)(({ theme }) => ({
    width: '150px',
    height: '150px',
    position: 'absolute',
    left: '30px',
    top: '100px',
    border: '8px solid white',
    [theme.breakpoints.down('sm')]: {
        width: '100px',
        height: '100px',
        left: '15px',
    },
}))

const MainInfoInnerBox = styled(Box)(({ theme }) => ({
    marginTop: '15px',
    marginLeft: '30px',
    marginRight: '30px',
    [theme.breakpoints.down('sm')]: {
        marginLeft: '15px',
    },
}))

const Name = styled(Typography)(({ theme }) => ({
    fontSize: '30px',
    fontWeight: 500,
    fontFamily: 'Space Grotesk',
    [theme.breakpoints.down('sm')]: {
        fontSize: '24px',
    },
}))

const CapabilitiesBox = styled(Box)(() => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginTop: '10px',
}))

const CapabilityChip = styled(Chip)(({ theme }) => ({
    fontWeight: 600,
    color: '#fff',
    backgroundColor: theme.palette.primary.main,
    margin: '4px',
}))

const SectionBox = styled(Box)(({ theme }) => ({
    background: '#FFFFFF',
    borderRadius: '10px',
    padding: '20px 30px 20px 30px',
    marginBottom: '15px',
    [theme.breakpoints.down('sm')]: {
        padding: '20px 15px 20px 15px',
    },
}))

const SectionTitle = styled(Typography)(() => ({
    fontWeight: 500,
    fontFamily: 'Space Grotesk',
}))

const HeroVideoSection = styled(Box)(({ theme }) => ({
    marginTop: '20px',
    marginBottom: '30px',
    [theme.breakpoints.down('sm')]: {
        marginTop: '10px',
        marginBottom: '20px',
    },
}))

const HeroTitle = styled(Typography)(({ theme }) => ({
    fontSize: '22px',
    fontWeight: 500,
    fontFamily: 'Space Grotesk',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        fontSize: '18px',
    },
}))

const MenuButtonBox = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '20px',
    marginRight: '15px',
}))

const BannerLogo = styled('img')(({ theme }) => ({
    width: '200px',
    [theme.breakpoints.down('sm')]: {
        width: '120px',
    },
}))

const StyledIconButton = styled(IconButton)(() => ({
    marginRight: '20px',
}))

const JobTitle = styled(Typography)(({ theme }) => ({
    fontSize: '22px',
    fontWeight: 400,
    color: theme.palette.primary.main,
    marginTop: '5px',
    [theme.breakpoints.down('sm')]: {
        fontSize: '18px',
    },
}))

// Define a type for assessment responses
interface AssessmentResponse {
    skillsAssessmentId: string;
    jobID: string;
    title: string;
    type: string;
    videoResponse?: {
        id: string;
        name: string;
        thumbnail: string | null;
        media: string;
        sizeMB: number;
        uploadComplete: boolean;
    };
    videoTranscript: string | null;
    textResponse: string | null;
}

type CandidatePublicProfileProps = {
    data: any
}

export function CandidatePublicProfile({ data }: CandidatePublicProfileProps) {
    const tooltipLeaveDelay = 200
    const [isContactDialogOpen, setIsContactDialogOpen] = React.useState<boolean>(false)
    const [copyTooltipText, setCopyTooltipText] = React.useState('Copy Profile Link')

    const handleClickLinkCopy = () => {
        const url = window.location.href
        navigator.clipboard.writeText(url)
        setCopyTooltipText('Copied')
    }

    const handleMouseLeaveCopy = () => {
        setTimeout(() => {
            setCopyTooltipText('Copy Profile Link')
        }, tooltipLeaveDelay)
    }

    if (!data) {
        return <></>
    }

    const hasApplication = data.applicationPublicProfile !== null
    const hasAssessments = data.assessmentResponses && Object.keys(data.assessmentResponses).length > 0

    // Get first video assessment for hero section
    const allAssessments = hasAssessments ? Object.values(data.assessmentResponses) as AssessmentResponse[] : []
    const videoAssessments = allAssessments.filter((assessment: AssessmentResponse) => assessment.type === 'VIDEO')
    const firstVideoAssessment = videoAssessments.length > 0 ? videoAssessments[0] : null

    // Prepare hero video URL
    let heroVideoUrl = '';
    if (firstVideoAssessment?.videoResponse?.media) {
        const mediaUrl = getMediaUrl(firstVideoAssessment.videoResponse.media as string);
        if (mediaUrl) {
            heroVideoUrl = mediaUrl;
        }
    }

    // Prepare application details for tabs if available
    const applicationDetails = hasApplication ? {
        questionnaire: data.applicationPublicProfile.questionnaire,
        jobTitle: data.applicationPublicProfile.jobTitle,
        applyDate: data.applicationPublicProfile.applyDate
    } : undefined

    // Prepare match score for tabs if available
    const matchScore = hasApplication ? {
        score: data.applicationPublicProfile.matchScore,
        details: data.applicationPublicProfile.matchScoreDetails
    } : undefined

    return (
        <Container maxWidth="md">
            <StyledBox>
                <MainInfoBox>
                    <BannerGradient>
                        <Button href="https://assembly-industries.com">
                            <BannerLogo src={logoWhite} alt="logo" />
                        </Button>
                    </BannerGradient>
                    <ProfilePicture
                        sx={{ bgcolor: 'primary.main' }}
                        src={data?.profilePicture?.media
                            ? getMediaUrl(data.profilePicture.media) || ''
                            : ''}
                    />
                    <MenuButtonBox>
                        <Tooltip title={copyTooltipText}>
                            <StyledIconButton
                                onClick={handleClickLinkCopy}
                                onMouseLeave={handleMouseLeaveCopy}
                            >
                                <LinkIcon />
                            </StyledIconButton>
                        </Tooltip>

                        <Button
                            variant="contained"
                            onClick={() => setIsContactDialogOpen(true)}
                        >
                            Contact Candidate
                        </Button>
                    </MenuButtonBox>
                    <MainInfoInnerBox>
                        <Name>
                            {data?.firstName} {data?.lastName}
                        </Name>
                        {hasApplication && (
                            <JobTitle>{data.applicationPublicProfile.jobTitle}</JobTitle>
                        )}
                        {data.bio && (
                            <Box
                                sx={{
                                    mt: 4,
                                    mb: 4,
                                    p: 4,
                                    backgroundColor: 'white',
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                    border: '1px solid',
                                    borderColor: 'grey.100',
                                    position: 'relative',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '4px',
                                        height: '100%',
                                        backgroundColor: 'primary.main',
                                        borderTopLeftRadius: '16px',
                                        borderBottomLeftRadius: '16px'
                                    }
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        whiteSpace: 'pre-line',
                                        fontFamily: 'Roboto',
                                        lineHeight: '1.8',
                                        color: 'text.primary',
                                        fontSize: '1.15rem',
                                        fontWeight: 400,
                                        letterSpacing: '0.01em'
                                    }}
                                >
                                    {data.bio}
                                </Typography>
                            </Box>
                        )}
                    </MainInfoInnerBox>
                </MainInfoBox>

                {/* Hero Video - First Skill Assessment */}
                {firstVideoAssessment && heroVideoUrl && (
                    <SectionBox>
                        <HeroVideoSection>
                            <HeroTitle>{firstVideoAssessment.title}</HeroTitle>
                            <VideoPlayer url={heroVideoUrl} />
                        </HeroVideoSection>
                    </SectionBox>
                )}

                {/* Skills and Assessments Tabs */}
                {(hasAssessments || hasApplication || data.resume) && (
                    <SectionBox>
                        <VideoAndAssessmentTabs
                            assessmentResponses={data.assessmentResponses || {}}
                            applicationDetails={applicationDetails}
                            resumeUrl={data.resume?.media ? getMediaUrl(data.resume.media) || '' : ''}
                            matchScore={matchScore}
                            portfolio={data.portfolio}
                        />
                    </SectionBox>
                )}

                {/* Capabilities (if available) */}
                {data.advertisedCapabilities && data.advertisedCapabilities.length > 0 && (
                    <SectionBox>
                        <SectionTitle variant="h5">Capabilities</SectionTitle>
                        <CapabilitiesBox>
                            {data.advertisedCapabilities.map((capability: any, index: number) => (
                                <CapabilityChip
                                    key={index}
                                    label={capability.name}
                                />
                            ))}
                        </CapabilitiesBox>
                    </SectionBox>
                )}

                {/* Skills (if available) */}
                {data.advertisedSkills && data.advertisedSkills.length > 0 && (
                    <SectionBox>
                        <SectionTitle variant="h5">Skills</SectionTitle>
                        <CapabilitiesBox>
                            {data.advertisedSkills.map((skill: any, index: number) => (
                                <CapabilityChip
                                    key={index}
                                    label={skill.name}
                                />
                            ))}
                        </CapabilitiesBox>
                    </SectionBox>
                )}
            </StyledBox>

            {/* Contact Dialog */}
            <ContactCandidateDialog
                open={isContactDialogOpen}
                onClose={() => setIsContactDialogOpen(false)}
                id={data.id}
            />
        </Container>
    )
}

// Helper component to display video content
export function VideoPlayer({ url }: { url: string }) {
    return (
        <Box
            component="video"
            sx={{
                width: '100%',
                maxHeight: '400px',
                borderRadius: '8px',
            }}
            controls
        >
            <source src={url} type="video/webm" />
            Your browser does not support the video tag.
        </Box>
    )
} 